import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Link } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import {
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { handleUserLogin } from "../../redux/reducers/authReducer";
import { unwrapResult } from "@reduxjs/toolkit";
import { FaUser, FaLock } from "react-icons/fa";
import Logo from "../../assets/images/logo-red1.png";
import BackgroundLogin from "../../assets/images/g10.png";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Container = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  flex-direction: ${({ lang }) => (lang === "ar" ? "row" : "row-reverse")};

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    overflow: auto;
  }
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  padding: 5vw;
  width: 100%;

  @media (max-width: 768px) {
    padding: 15px;
  }

  form {
    max-width: 400px;
    width: 100%;
    padding: 40px;
    border-radius: 10px;
    @media (max-width: 768px) {
      padding: 5px;
    }
  }

  h2 {
    text-align: center;
    margin-bottom: 30px;
    color: #ae1a1a;
  }
`;

const InfoSection = styled.div`
  flex: 2;
  background-color: #ae1a1a;
  background-image: url(${BackgroundLogin});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 80%; /* أو حسب الحاجة */
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 80px 0; /* فراغ من فوق وتحت */

  @media (max-width: 768px) {
    display: none;
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`;

const Icon = styled.span`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #ae1a1a;
`;

const InputField = styled.input`
  width: 100%;
  padding: 10px 10px 10px 35px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  box-sizing: border-box;
  &:focus + label,
  &:not(:placeholder-shown) + label {
    top: -10px;
    left: 10px;
    font-size: 12px;
    color: #ae1a1a;
  }
`;

const Label = styled.label`
  position: absolute;
  left: ${({ lang }) => (lang === "ar" ? "auto" : "35px")};
  right: ${({ lang }) => (lang === "ar" ? "35px" : "auto")};
  top: 50%;
  transform: translateY(-50%);
  transition: 0.3s;
  color: gray;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 10px;
  margin-top: 20px;
  background: ${({ disabled }) => (disabled ? "#f8c7c7" : "#ae1a1a")};
  color: white;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: 0.3s;
  &:hover {
    background: ${({ disabled }) =>
      disabled ? "#f8c7c7" : "rgb(210, 38, 38)"};
  }
`;

export default function UserLogin(props) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false); // حالة Checkbox
  const { setStep } = props;
  const { companyData } = useSelector((state) => ({
    companyData: state.Auth.companyData,
  }));
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("auth");
  const isRTL = i18n.language === "ar";
  const handleClose = () => {
    setError("");
  };

  const schema = yup.object({
    username: yup.string().required(t("Login.UserName_Error")),
    password: yup.string().required(t("Login.Password_Error")),
  });

  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      username: "",
      password: "",
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = methods;

  const submitForm = (data) => {
    dispatch(
      handleUserLogin({
        data: data,
        setLoading: setLoading,
      })
    )
      .then(unwrapResult)
      .catch(() => {
        setLoading(false);
        setError(t("Login.UserName_Error"));
      });
  };

  return (
    <Container>
      <InfoSection></InfoSection>
      <FormSection>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)} style={{ width: "100%" }}>
            <img src={Logo} alt="logo" width="150px" />
            <h2
              style={{
                color: "#474343",
                marginBottom: "20px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("Login.Login_Header")}
            </h2>
            <div
              style={{
                color: "#757373",
                marginBottom: "20px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("Login.Login_Header_Text")}
            </div>

            <InputContainer>
              <Icon>
                <FaUser />
              </Icon>
              <InputField
                type="text"
                {...register("username")}
                required
                placeholder=" "
              />
              <Label lang={i18n.language}>{t("Login.UserName_Label")}</Label>
            </InputContainer>
            {errors.username && (
              <p style={{ color: "red", marginBottom: "10px" }}>
                {errors.username.message}
              </p>
            )}

            <InputContainer>
              <Icon>
                <FaLock />
              </Icon>
              <InputField
                type="password"
                {...register("password")}
                required
                placeholder=" "
              />
              <Label lang={i18n.language}>{t("Login.Password_Label")}</Label>
            </InputContainer>
            {errors.password && (
              <p style={{ color: "red", marginBottom: "10px" }}>
                {errors.password.message}
              </p>
            )}
            {/* ✅ Checkbox الموافقة على الشروط */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                style={{ marginRight: isRTL ? 0 : "15px" }}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label={t("Login.Terms_Conditions")}
              />
              <span
                style={{
                  color: "#ae1a1a",
                  cursor: "pointer",
                  fontWeight: "bold",
                  marginLeft: isRTL ? "0" : "5px",
                }}
                onClick={() => {
                  navigate("/forget-password");
                }}
              >
                {t("Login.forget_password")}
              </span>
            </div>

            <SubmitButton type="submit">
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Login.Login_Submit")
              )}
            </SubmitButton>
          </form>
          <div
            style={{
              color: "#757373",
              marginBottom: "20px",
              textAlign: isRTL ? "right" : "left",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {t("Login.aliens_Description")}{" "}
            <span
              style={{
                color: "#ae1a1a",
                cursor: "pointer",
                fontWeight: "bold",
                marginLeft: isRTL ? 0 : "5px",
              }}
              onClick={() => {
                navigate("/auth/signup");
              }}
            >
              {t("Login.signup")}
            </span>
          </div>

          <span
            style={{
              color: "#ae1a1a",
              cursor: "pointer",
              fontWeight: "bold",
              marginLeft: isRTL ? 0 : "5px",
            }}
            onClick={() => {
              console.log("navigating...");
              navigate("/tracking");
            }}
          >
            {t("Login.track_order")}
          </span>
        </FormProvider>
      </FormSection>

      {/* ✅ Snackbar لإظهار الأخطاء */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
